/**
 * Cliente HTTP para a API Novità.
 *
 * - Lê a URL base de VITE_API_URL (default: http://localhost:8000 em dev).
 * - Anexa automaticamente o Bearer token do Firebase Auth quando o usuário está logado.
 * - Trata erros de domínio (`{ code, message }`) lançando NovitaApiError com mensagem em pt-BR.
 *
 * Uso:
 *   import { api } from '@/api/client';
 *   const planos = await api.get<Plano[]>('/api/planos');
 *   const checkout = await api.post('/api/assinaturas/checkout', { ... });
 */
import { auth } from '../firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8000';

export class NovitaApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(await authHeader()),
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const resp = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // 204 No Content
  if (resp.status === 204) return undefined as T;

  const text = await resp.text();
  let data: any;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }

  if (!resp.ok) {
    const detail = data?.detail || data;
    const code = detail?.code || 'erro_api';
    const message =
      detail?.message ||
      detail?.detail ||
      `Erro ${resp.status} ao chamar ${path}`;
    throw new NovitaApiError(message, code, resp.status);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
